<template>
  <div class="w-full relative">
    <!-- hero  -->
    <div class="bg-purple w-full pt-16 lg:pt-32">
      <div class="container mx-auto">
        <h1 class="hidden text-purple">{{ page.fields.metaTitle }}</h1>
        <lottie
          class="hero-animation"
          :width="1244"
          :height="700"
          :options="lottieOptions"
          @animCreated="handleAnimation"
        />
        <div
          class="text-center lg:text-xl pb-8 font-bold text-white mx-auto w-full px-4 max-w-xl"
        >
          {{ page.fields.description }}
        </div>
        <div
          v-if="info.subheading"
          class="flex font-bold text-white mb-4 items-center justify-center mx-auto w-full px-4 max-w-md"
        >
          {{ info.subheading }}
        </div>
        <div
          class="flex items-center justify-center mx-auto w-full px-4 max-w-md"
        >
          <div
            v-for="(sponsor, s) in info.sponsors"
            :key="s"
            class="w-1/2 px-4"
          >
            <img
              class="h-20 lg:h-24"
              :src="sponsor.image.src"
              :alt="sponsor.image.alt"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="bg-purple w-full py-12 relative">
      <div
        class="w-full max-w-xl flex relative items-center justify-center md:px-4 mx-auto"
      >
        <div class="hidden lg:block w-48 h-full pr-8">
          <lottie
            class="max-w-full"
            :width="180"
            :height="300"
            :options="lottieMOptions"
          />
        </div>

        <video
          v-if="
            page.fields.linkedVideo &&
            page.fields.linkedVideo.fields &&
            page.fields.linkedVideo.fields.video &&
            page.fields.linkedVideo.fields.video.fields
          "
          class="object-cover"
          controls
          :poster="
            page.fields.linkedVideo.fields.image
              ? page.fields.linkedVideo.fields.image.fields.file.url
              : ''
          "
        >
          <source
            :src="page.fields.linkedVideo.fields.video.fields.file.url"
            :type="page.fields.linkedVideo.fields.video.fields.file.contentType"
          />
        </video>

        <div class="hidden lg:block py-4 w-48 h-full pl-12">
          <lottie
            class="max-w-full"
            :width="180"
            :height="300"
            :options="lottiePOptions"
          />
        </div>
      </div>
    </div>

    <div
      class="relative -mt-1 bg-curve bg-no-repeat w-full bg-contain bg-right-top"
    >
      <div class="w-full max-w-xs md:max-w-xl mx-auto px-8">
        <h2
          v-if="info.heading"
          class="text-white font-bold w-full uppercase text-xl lg:text-3xl text-center mb-8 leading-snug"
        >
          {{ info.heading }}
        </h2>
      </div>
      <div
        v-if="info.labs"
        class="relative z-10 w-full flex flex-col items-center justify-center mx-auto p-0 md:px-4 md:flex-row md:flex-wrap lg:px-8"
      >
        <div
          v-for="(lab, i) in info.labs"
          :key="i"
          class="w-full lab lg:w-1/2 px-4 py-2 lg:p-4"
        >
          <div
            class="bg-lilac text-white relative w-full h-full text-center py-4 px-4 rounded-md shadow-md"
          >
            <img
              v-if="lab.image && lab.image.src"
              class="mx-auto block mb-6 mt-4"
              :src="lab.image.src"
              :alt="lab.image.alt"
            />
            <div
              v-if="lab.heading"
              class="font-bold uppercase text-xl lg:text-2xl leading-none mb-3"
              v-html="lab.heading"
            ></div>
            <div
              v-if="lab.text"
              class="font-semibold mx-auto max-w-lg px-4 text-sm md:text-base"
              v-html="lab.text"
            ></div>
          </div>
        </div>
      </div>
      <div class="container relative z-10 mx-auto px-4 lg:px-8 py-2 lg:py-4">
        <div
          v-if="info.pricing && info.pricing.cols"
          class="w-full mb-8 flex text-white flex-wrap justify-center bg-lilac px-4 lg:px-8 py-4 rounded-md shadow-md"
        >
          <div
            v-for="(col, b) in info.pricing.cols"
            :key="b"
            class="w-full lg:w-1/2"
          >
            <h3
              class="text-xl text-center lg:text-left mb-4 leading-none lg:text-3xl font-bold uppercase"
            >
              {{ col.heading }}
            </h3>
            <div
              v-if="col.lab_pricing_breakdown"
              class="w-full flex flex-wrap lg:pr-4 mb-2"
            >
              <div
                v-for="(lp, p) in col.lab_pricing_breakdown"
                :key="p"
                :class="`${lp.class} w-full text-center lg:text-left lg:text-xl font-bold uppercase lg:w-1/2`"
              >
                {{ lp.name }} - {{ lp.price }}
              </div>
            </div>
            <div
              v-if="col.text"
              class="font-bold text-center text-sm lg:text-base lg:text-left lg:pr-32 info"
              v-html="col.text"
            ></div>
            <p v-if="col.legal" class="font-thin text-sm lg:pr-32 leading-snug">
              {{ col.legal }}
            </p>

            <div v-if="col.info" :class="`flex flex-wrap ${col.classes}`">
              <div v-for="(info, d) in col.info" :key="d" :class="info.classes">
                <h3
                  v-if="info.heading"
                  class="text-xl lg:text-2xl leading-none text-center lg:text-left font-bold mb-4 uppercase"
                  v-html="info.heading"
                ></h3>
                <p
                  v-if="info.pricing"
                  class="font-bold uppercase text-center lg:text-left"
                >
                  {{ info.pricing }}
                </p>
                <div v-if="info.text" class="text-xs" v-html="info.text"></div>
              </div>
            </div>
            <div v-if="col.addons" class="py-4 px-2 lg:p-4 flex">
              <div
                v-for="(addon, a) in col.addons"
                :key="a"
                class="w-full text-sm lg:text-base text-center lg:text-left"
              >
                <h3
                  v-if="addon.heading"
                  class="font-bold mb-2 uppercase"
                  v-html="addon.heading"
                ></h3>
                <div class="flex flex-wrap">
                  <div
                    v-for="(item, n) in addon.items"
                    :key="n"
                    class="w-1/2 lg:w-1/4"
                  >
                    {{ item }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="info.legal_info" class="relative z-10 w-full pb-8">
        <div
          class="mx-auto w-full max-w-xl px-8 text-center text-xs leading-tight font-light"
        >
          {{ info.legal_info }}
        </div>
      </div>
      <div v-if="page.fields && page.fields.children" class="relative z-10">
        <PageSections :sections="page.fields.children" />
      </div>

      <svg
        class="absolute bottom-0 right-0 z-0 -mr-8 base-dash-circle animate-spin overflow-visible"
        xmlns="http://www.w3.org/2000/svg"
        width="254"
        height="254"
        viewBox="0 0 254 254"
      >
        <circle
          id="Oval"
          cx="124.5"
          cy="124.5"
          r="124.5"
          transform="translate(2.5 2.5)"
          fill="none"
          stroke="#8a0ffe"
          stroke-miterlimit="10"
          stroke-width="5"
          stroke-dasharray="10 20"
        />
      </svg>

      <svg
        class="absolute bottom-0 right-0 z-0 -mr-12 base-circle overflow-visible"
        xmlns="http://www.w3.org/2000/svg"
        width="600"
        height="600"
        viewBox="0 0 600 600"
      >
        <circle
          id="Oval"
          cx="237.5"
          cy="237.5"
          r="237.5"
          transform="translate(2.5 2.5)"
          fill="none"
          stroke="#000"
          stroke-miterlimit="10"
          stroke-width="5"
        />
      </svg>

      <img
        class="absolute bottom-0 right-0 z-0 mr-12 mb-32"
        src="//images.ctfassets.net/3dar4x4x74wk/5ME20o1twhBGSfdGnrFXpF/c1226094b2d88545fb02bbc7366f4637/protractor.svg"
        alt="protractor"
      />
      <svg
        class="absolute bottom-0 left-0 z-0 base-oval overflow-visible"
        xmlns="http://www.w3.org/2000/svg"
        width="842"
        height="842"
        viewBox="0 0 842 842"
      >
        <circle id="Oval" cx="842" cy="842" r="842" fill="#b85eff" />
      </svg>
    </div>
  </div>
</template>

<script>
import lottie from 'vue-lottie/src/lottie.vue';
import * as animationData from '@/assets/config/me_play_academy.json';
import * as microscope from '@/assets/config/microscope.json';
import * as pi from '@/assets/config/pie-lambda-eye.json';
import info from '~/assets/config/playAcademyDetails.json';
import PageSections from '~/components/PageSections';

export default {
  components: {
    PageSections,
    lottie,
  },
  async asyncData({ $api, params }) {
    return {
      page: await $api.getPage('play-academy', 'event'),
    };
  },
  data() {
    return {
      page: {
        fields: {},
      }, 
      info,
      loading: false,
      anim: null, // for saving the reference to the animation
      lottieOptions: { animationData: animationData.default },
      lottieMOptions: { animationData: microscope.default },
      lottiePOptions: { animationData: pi.default },
    };
  },
  async validate({ $api, params }) {
    return await $api.getPage('play-academy', 'event');
  },

  beforeMount() {
    if (
      this.$route.params.category != this.page.fields.eventType[0].fields.slug
    ) {
      this.$router.push(
        `/events/${this.page.fields.eventType[0].fields.slug}/${this.page.fields.slug}`
      );
    }
  },
  methods: {
    handleAnimation(anim) {
      this.anim = anim;
    },
    start() {
      this.loading = true;
    },
    finish() {
      this.loading = false;
    },
  },
  head() {
    return this.$metadata(this.page);
  },
};
</script>
<style lang="postcss">

.lab {
  height: 425px;
}

.base-dash-circle {
  margin-bottom: 160vh;
  animation: spin 30s linear infinite;
  @screen lg {
    margin-bottom: 40vh;
  }
}
.base-circle {
  margin-bottom: 160vh;
  @screen lg {
    margin-bottom: 35vh;
  }
}
.base-oval {
  margin-left: -50vw;
  margin-bottom: -50vh;
}
.info p {
  @apply mb-4 leading-none;
}
.hero-animation {
  max-height: 220px;
  max-width: 360px;
  @apply mx-auto transition-all duration-200 ease-linear;

  @screen sm {
    max-height: 360px;
    max-width: 620px;
  }

  @screen md {
    max-height: 420px;
    max-width: 768px;
  }

  @screen lg {
    max-height: 570px;
    max-width: 1020px;
  }
}
.bg-curve {
  background-image: url('https://images.ctfassets.net/3dar4x4x74wk/4iSVkXMnH2NTsPDcNZifDZ/3ae2b60fb32e5490897467ab21a48732/purple-curve-bg.svg');
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
